import DOMPurify from "dompurify";
import { useEffect, useMemo, useState } from "react";

import {
  buildMermaidSource,
  createMermaidCacheKey,
  MERMAID_MAX_UPSCALE,
  prepareMermaidSvg,
  type MermaidFigureAlign,
  estimateMermaidSvgSize,
  isRenderableMermaidCandidate,
  nextMermaidRenderId,
  renderedMermaidCache,
  resolveMermaidRenderDelay,
  type MermaidTheme,
} from "./MermaidDiagram.logic";

type MermaidModule = typeof import("mermaid")["default"];

let mermaidModulePromise: Promise<MermaidModule> | null = null;

/**
 * Mermaid is ~2 MB, so it only loads the first time a message carries a
 * mermaid fence. `strict` security keeps labels sanitised (DOMPurify) and
 * blocks click handlers and external links inside diagrams.
 */
function loadMermaid(): Promise<MermaidModule> {
  if (mermaidModulePromise == null) {
    mermaidModulePromise = import("mermaid")
      .then((module) => {
        module.default.initialize({
          startOnLoad: false,
          securityLevel: "strict",
          fontFamily: "inherit",
          // Plain SVG text labels: nothing HTML rides inside the diagram, so
          // the sanitiser below can keep to the SVG profile, and exported
          // PNGs render the labels without a foreignObject round trip.
          htmlLabels: false,
          flowchart: { htmlLabels: false },
        });
        return module.default;
      })
      .catch((error: unknown) => {
        mermaidModulePromise = null;
        throw error;
      });
  }
  return mermaidModulePromise;
}

/** Returns the SVG, or null when the source doesn't parse (e.g. still streaming). */
async function renderMermaidSvg(source: string): Promise<string | null> {
  const mermaid = await loadMermaid();
  const parsed = await mermaid.parse(source, { suppressErrors: true });
  if (parsed === false) {
    return null;
  }
  const { svg } = await mermaid.render(nextMermaidRenderId(), source);
  return svg;
}

export interface MermaidSvgState {
  /** Rendered markup, or null until the first successful render. */
  readonly svg: string | null;
  /** True when `svg` belongs to an earlier version of a still-changing fence. */
  readonly stale: boolean;
  /**
   * True when the current body was tried and cannot render: it does not
   * parse, the renderer threw, or it is too short to be a diagram at all.
   * Callers use it to stop waiting and show the source.
   */
  readonly failed: boolean;
}

/**
 * Renders a mermaid fence to SVG off the React tree. While the message
 * streams the fence body is usually incomplete, so renders are debounced and
 * a failed parse keeps the previous diagram (or nothing, before the first).
 * `enabled: false` skips the work for fences another renderer handles.
 */
export function useMermaidSvg(
  code: string,
  theme: MermaidTheme,
  isStreaming: boolean,
  enabled: boolean,
): MermaidSvgState {
  const cacheKey = createMermaidCacheKey(code, theme);
  const [rendered, setRendered] = useState<{ key: string; svg: string } | null>(() => {
    if (!enabled) return null;
    const cached = renderedMermaidCache.get(cacheKey);
    return cached == null ? null : { key: cacheKey, svg: cached };
  });
  const [failedKey, setFailedKey] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || rendered?.key === cacheKey) {
      return;
    }
    const cached = renderedMermaidCache.get(cacheKey);
    if (cached != null) {
      setRendered({ key: cacheKey, svg: cached });
      return;
    }
    if (!isRenderableMermaidCandidate(code)) {
      return;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      renderMermaidSvg(buildMermaidSource(code, theme))
        .then((svg) => {
          if (cancelled) {
            return;
          }
          if (svg == null) {
            setFailedKey(cacheKey);
            return;
          }
          renderedMermaidCache.set(cacheKey, svg, estimateMermaidSvgSize(svg));
          setRendered({ key: cacheKey, svg });
        })
        .catch((error: unknown) => {
          if (cancelled) {
            return;
          }
          // A parse that passed but a render that failed is a mermaid bug for
          // that diagram type; keep whatever was on screen and log it.
          console.warn(
            "[chat-markdown] mermaid render failed, keeping source view",
            error instanceof Error ? error.message : error,
          );
          setFailedKey(cacheKey);
        });
    }, resolveMermaidRenderDelay(isStreaming));

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [cacheKey, code, enabled, isStreaming, rendered?.key, theme]);

  // A fence that another renderer now owns must not show this hook's last
  // diagram; hide the result while disabled rather than clearing state.
  if (!enabled) return { svg: null, stale: false, failed: false };
  return {
    svg: rendered?.svg ?? null,
    stale: rendered != null && rendered.key !== cacheKey,
    failed: failedKey === cacheKey || !isRenderableMermaidCandidate(code),
  };
}

/**
 * Mermaid (securityLevel strict) already sanitises label text, but the markup
 * it emits is still injected as HTML; a second pass over the whole SVG means
 * nothing executable can ride along even if a diagram type slips. Labels are
 * SVG text (see `htmlLabels` above), so the SVG profiles are enough.
 */
const MERMAID_SVG_SANITIZE = { USE_PROFILES: { svg: true, svgFilters: true } } as const;

export function MermaidSvg({
  svg,
  stale,
  align,
}: {
  readonly svg: string;
  readonly stale: boolean;
  readonly align: MermaidFigureAlign;
}) {
  const prepared = useMemo(() => {
    const { svg: preparedSvg, naturalWidth } = prepareMermaidSvg(svg, align);
    return { naturalWidth, svg: DOMPurify.sanitize(preparedSvg, MERMAID_SVG_SANITIZE) };
  }, [align, svg]);
  return (
    <div
      className="chat-markdown-mermaid"
      data-stale={stale ? "true" : undefined}
      role="img"
      aria-label="Diagram"
      style={
        prepared.naturalWidth != null
          ? { maxWidth: `${Math.round(prepared.naturalWidth * MERMAID_MAX_UPSCALE)}px` }
          : undefined
      }
      dangerouslySetInnerHTML={{ __html: prepared.svg }}
    />
  );
}
